import React, { FC } from 'react';

import {
  encodeApiQueryParamsForPageUrl,
  parseCoordinate,
} from '@rover/react-lib/src/pages/training/TrainerSearchPage/params';
import type { HomepageSearchFilters } from '@rover/types/src/HomepageSearchFilters';

interface Props {
  filters: HomepageSearchFilters;
  spacesRequired?: number;
  bypassInterstitial?: boolean;
  isDogTraining?: boolean;
}

const HiddenInput: FC<{
  name: string;
  value: string | number | undefined | null;
  testid?: string;
}> = ({ name, value, testid }) =>
  value === undefined || value === null || value === '' ? null : (
    <input
      type="hidden"
      name={name}
      value={String(value)}
      data-testid={testid ?? `search-box-hidden-${name}`}
    />
  );

/**
 * The training search page only accepts a subset of the HomepageSearchFilters
 */
const TrainerSearchBoxHiddenInputs: FC<{ filters: HomepageSearchFilters }> = ({ filters }) => {
  const urlSearchParams = encodeApiQueryParamsForPageUrl({
    location: filters.location,
    centerlat: parseCoordinate(filters.centerlat),
    centerlng: parseCoordinate(filters.centerlng),
    locationType: filters.locationType,
    rawLocationTypes: filters.rawLocationTypes,
  });

  return (
    <>
      <HiddenInput name="location" value={urlSearchParams.get('location')} />
      <HiddenInput name="centerlat" value={urlSearchParams.get('centerlat')} />
      <HiddenInput name="centerlng" value={urlSearchParams.get('centerlng')} />
      <HiddenInput name="location_type" value={urlSearchParams.get('location_type')} />
      {/* The trainer search page expects multiple params for raw location types instead of a single param with a comma-joined string */}
      {urlSearchParams.getAll('raw_location_types')?.map((value) => (
        <HiddenInput
          key={`raw_location_types-${value}`}
          name="raw_location_types"
          value={value}
          testid={`search-box-hidden-raw_location_types-${value}`}
        />
      ))}
    </>
  );
};

const SearchBoxHiddenInputs: FC<Props> = ({
  filters,
  spacesRequired,
  bypassInterstitial,
  isDogTraining,
}) => {
  if (isDogTraining) {
    return <TrainerSearchBoxHiddenInputs filters={filters} />;
  }

  const rawLocationTypes = filters.rawLocationTypes?.length
    ? filters.rawLocationTypes.join(',')
    : undefined;

  return (
    <>
      <HiddenInput name="service_type" value={filters.serviceTypeSlug} />
      <HiddenInput name="location" value={filters.location} />
      <HiddenInput name="centerlat" value={filters.centerlat} />
      <HiddenInput name="centerlng" value={filters.centerlng} />
      <HiddenInput name="location_type" value={filters.locationType} />
      <HiddenInput name="raw_location_types" value={rawLocationTypes} />
      <HiddenInput name="spaces_required" value={spacesRequired} />
      {bypassInterstitial ? (
        <input
          type="hidden"
          name="bypass_interstitial"
          value="true"
          data-testid="search-box-hidden-bypass_interstitial"
        />
      ) : null}
    </>
  );
};

export default SearchBoxHiddenInputs;
